// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';

import {FlatSortConfig, SortBy, SortConfig, SortOrder} from 'common/report/util/sorting/types';
import {BarSelection} from 'common/charts/visualization/conversion-column-viz';
import {ConversionStepBarType, NormalConversionStep} from 'common/charts/visualization/conversion-column-viz/util';
import {
  BehaviorShowClause,
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsResourceType,
  InsightsSeriesTree,
  MetricBehavior,
  ShowClause,
} from 'common/report/insights/util/types';
import {CountType, FunnelSegment} from 'common/report/funnels/models/types';
import {
  OTHER_PROP,
  OVERALL_PROP,
  OVERALL_PROP_NAME,
  OVERALL_PROP_NAME_PAST,
  TableToggleableMetrics,
} from 'common/report/funnels/models/constants';
import {
  getFunnelsShowClauses,
  getNumerOfEventComparisons,
  hasEventComparison,
} from 'common/report/insights/models/utils';
import {getDataGroupName, getSegmentDisplayName} from 'common/report/funnels/util';
import {getSegmentPropertyType} from 'common/query-builder/query-block/segment-menu/types';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {getGroupByProperties} from 'common/query-builder/query-block/segment-menu/util';
import {Report} from 'common/report/insights/models/report';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {flattenSeriesToDepth, formatDateRange, getSortConfig} from 'common/report/insights/util';
import {getCalendarDate} from 'common/util/date';
import {ChartType, PropertyType, ResourceType, SavedLegend} from 'common/report/constants';
import {propertiesUtils} from 'common/report/util/properties';
import {renameEvent} from 'common/query-builder/cohorts-query-builder/util';
import {MATH_TO_FUNNEL_STEPS_METRIC} from 'common/widgets/alerts/custom-alert/constants';
import {ColumnGroup, SegmentColumnSpec} from 'common/components/data-table/types';
import {DEFAULT_STEPS_TABLE_COLUMNS} from 'common/report/insights/visualization/constants';
import {
  GroupClause,
  isCohortGroupClause,
  isDateGroupClause,
  isNumericGroupClause,
  isOtherGroupClause,
} from 'common/report/insights/models/group-clause';
import {Translatable} from 'common/i18n';
import {SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {MetricsResource} from 'common/report/model-resources/metrics';

import {reconcileComparisonSegments, StepChartData} from '../util';
import {FunnelTableRow} from '../types';
import {getColumnGroups, getRows} from '../table-util';
import {StatSigClickHandler} from '../cell-renderers';
import {getCountType, getPropSumProp} from './util';

export class MultimetricStepBookmarkController {
  declare bookmark: InsightsBookmarkParams;
  declare showClauses?: ShowClause[];
  declare _queryResults: InsightsQueryResults<boolean | number>;
  declare _metricKeys: string[];

  constructor(bookmark: InsightsBookmarkParams, results: InsightsQueryResults<boolean | number>) {
    this.bookmark = bookmark;
    this.showClauses = bookmark.sections.show;
    this._queryResults = results;
  }

  getDisableViewInFlowsButtonMessage(): Translatable {
    let reason: Translatable = ``;
    const groupBy = this.bookmark.sections.group;

    const isTopPaths = (group: GroupClause): boolean => {
      if (group.resourceType !== InsightsResourceType.Other) {
        return false;
      }
      const isTopPathsChart = group.value === `$paths`;
      return isTopPathsChart;
    };

    if (Array.isArray(groupBy)) {
      if (groupBy.some(isTopPaths)) {
        reason = ({t}) => t(`chart-actions.funnels.disabled.flows-top-path`);
      } else if (groupBy.some(isNumericGroupClause)) {
        reason = ({t}) => t(`chart-actions.funnels.disabled.flows-numeric`);
      } else if (groupBy.some(isDateGroupClause)) {
        reason = ({t}) => t(`chart-actions.funnels.disabled.flows-date`);
      } else if (groupBy.some(isCohortGroupClause)) {
        reason = ({t}) => t(`chart-actions.funnels.disabled.flows-cohort`);
      }
    }

    if (hasEventComparison(this.bookmark?.sections?.show || [])) {
      reason = ({t}) => t(`chart-actions.funnels.disabled.flows-comparison`);
    }

    return reason;
  }

  public getDisableViewUsersButtonMessage(): Translatable {
    let reason: Translatable = ``;

    const isTopPaths = (groupClause: GroupClause): boolean => {
      if (!isOtherGroupClause(groupClause)) {
        return false;
      }
      const isTopPathsChart = groupClause.value === `$paths`;
      return isTopPathsChart;
    };
    const dataGroup = getDataGroupName();
    const groupBy = this.bookmark.sections.group;
    if (Array.isArray(groupBy)) {
      if (groupBy.some(isTopPaths)) {
        reason = ({t}) =>
          t(`chart-actions.funnels.disabled.view-users-top-path`, {context: dataGroup, group: dataGroup});
      } else if (groupBy.some(isCohortGroupClause)) {
        reason = ({t}) => t(`chart-actions.funnels.disabled.view-users-cohort`, {context: dataGroup, group: dataGroup});
      }
    }

    return reason;
  }

  set queryResults(results: InsightsQueryResults) {
    this._queryResults = results;
    this._metricKeys = null;
  }

  get queryResults(): InsightsQueryResults<boolean | number> {
    return this._queryResults;
  }

  get metricKeys(): string[] {
    if (this._metricKeys) return this._metricKeys;

    let segmentationLevel = this.getBreakdownCount();
    let metrics = Object.values(this.queryResults.series)[0];
    while (segmentationLevel > 0) {
      const metricKey = Object.keys(metrics)[0];
      metrics = Object.values(metrics)[0];
      // Stop the loop if the key is "$overall" or "$other" because it will be followed by metrics
      if (metricKey === OVERALL_PROP || metricKey === OTHER_PROP) {
        break;
      }
      segmentationLevel -= 1;
    }
    const metricKeys = Object.keys(metrics[`count`]);
    // Store computed keys for later use
    this._metricKeys = metricKeys;
    return metricKeys;
  }

  public getBookmarkWithAppliedDefaults(bookmark: InsightsBookmarkParams) {
    return bookmark;
  }

  public getConversionSteps(): Array<NormalConversionStep<never, string>> {
    if (this.isEmptyReport()) {
      return null;
    }
    const normal = this.getBars(this.queryResults.series, ConversionStepBarType.Normal);

    if (this.queryResults.time_comparison) {
      const comparison = this.getBars(this.queryResults.time_comparison.series, ConversionStepBarType.Comparison);
      return normal.map((stepInfo, idx) => {
        const comparisonMap = new Map(comparison[idx].bars.map((bar) => [bar.extras, bar]));
        return {
          ...stepInfo,
          bars: stepInfo.bars.flatMap((bar) => [
            bar,
            comparisonMap.get(bar.extras) || {
              ...bar,
              value: 0,
              conversionRate: 0,
              type: ConversionStepBarType.Comparison,
            },
          ]),
        };
      });
    } else {
      return normal;
    }
  }

  public getStepLabel(behaviors: MetricBehavior[], index: number): {label: string; queryLabel: string} {
    const numberOfComparisons = this.getNumberOfEventComparisons();
    const metricKeys = this.metricKeys;
    let label: string;
    if (numberOfComparisons > 0) {
      label = metricKeys[index];
    } else {
      const isAnyOrder = behaviors.some((behavior) => behavior.funnelOrder === `any`);
      const behavior = behaviors[index];
      if (isAnyOrder) {
        label = `Step ${index + 1}`;
      } else {
        label = behavior.renamed || renameEvent(behavior.name);
      }
    }
    const queryLabel = metricKeys[index];

    return {label, queryLabel};
  }

  public getBars(
    series: InsightsSeriesTree<boolean | number>,
    type: ConversionStepBarType,
  ): Array<NormalConversionStep<never, string>> {
    const behaviors = getFunnelsShowClauses(this.bookmark?.sections?.show || [])[0]?.behavior?.behaviors;
    // first level is always tree
    const metrics = Object.values(series)[0] as InsightsSeriesTree;
    return behaviors?.map((behavior, i) => {
      // TODO nix the numbered label generation for something more robust (like reading from meta field)
      const {label, queryLabel} = this.getStepLabel(behaviors, i);
      const breakdownCount = this.getBreakdownCount();
      let bars;

      if (breakdownCount === 0) {
        bars = [
          {
            displayName: OVERALL_PROP_NAME,
            extras: OVERALL_PROP,
            value: this._getMetric(metrics, queryLabel, this._getCountMetricName()),
            conversionRate: this._getMetric(metrics, queryLabel, `step_conv_ratio`),
            type,
          },
        ];
      } else {
        const flatKeys = flattenSeriesToDepth(metrics, breakdownCount - 1);
        bars = flatKeys.map(([keys, leaf]) => ({
          value: this._getMetric(leaf, queryLabel, this._getCountMetricName()),
          conversionRate: this._getMetric(leaf, queryLabel, `step_conv_ratio`),
          displayName: this.getSegmentLabelForPath(keys),
          extras: this.getSegmentLabelForPath(keys),
          type,
        }));
      }
      return {
        displayName: label,
        display: behavior.display,
        bars,
      };
    });
  }

  public getSelectedStepBars(): BarSelection[] {
    // TODO
    return [{stepIndex: 0, barIndex: 0, isDropoff: false}];
  }

  private getFunnelSegments(): {
    funnelSegments: FunnelSegment[];
    funnelCompareSegments: null | FunnelSegment[];
    funnelLiftSegments: null | FunnelSegment[];
  } {
    const series = this.queryResults?.series;
    const timeComparisonSeries = this.queryResults.time_comparison?.series;
    const liftComparisonSeries = this.queryResults.lift_comparison?.series;
    const funnelSegments = this._getFunnelSegments({series});
    let funnelCompareSegments: FunnelSegment[],
      funnelLiftSegments: FunnelSegment[] = null;
    if (timeComparisonSeries) {
      funnelCompareSegments = this._getFunnelSegments({series: timeComparisonSeries, isPast: true});
    }
    if (liftComparisonSeries) {
      funnelLiftSegments = this._getFunnelSegments({series: liftComparisonSeries});
    }
    return {funnelSegments, funnelCompareSegments, funnelLiftSegments};
  }

  private _getMetricsForSegment(segment: InsightsSeriesTree<any>, behaviors: MetricBehavior[], index: number) {
    const behavior = behaviors[index];
    const {label, queryLabel} = this.getStepLabel(behaviors, index);
    const getMetric = (metricName) => this._getMetric(segment, queryLabel, metricName);

    const pValue = getMetric(`p_value`);

    return {
      count: getMetric(this._getCountMetricName()),
      avg_time: getMetric(`avg_time`),
      avg_time_from_start: getMetric(`avg_time_from_start`),
      event: behavior.name,
      goal: behavior.name,
      step_label: label,
      overall_conv_ratio: getMetric(`overall_conv_ratio`),
      step_conv_ratio: getMetric(`step_conv_ratio`),
      display: behavior.display,
      index,
      ...(this.shouldShowStatSig() && isNumber(pValue?.p) ? {p_value: pValue} : {}),
    };
  }

  private _getFunnelSegments({
    series,
    isPast = false,
  }: {
    series: InsightsSeriesTree<boolean | number>;
    isPast?: boolean;
  }): FunnelSegment[] {
    if (!series) {
      return null;
    }
    const behaviors = getFunnelsShowClauses(this.bookmark?.sections?.show || [])[0]?.behavior?.behaviors;
    // first level is always tree
    const metrics = Object.values(series)[0] as InsightsSeriesTree;
    const breakdownCount = this.getBreakdownCount();

    if (!breakdownCount) {
      return [
        {
          prop: OVERALL_PROP,
          propName: isPast ? OVERALL_PROP_NAME_PAST : OVERALL_PROP_NAME,
          props: [
            {
              prop: OVERALL_PROP,
              propName: isPast ? OVERALL_PROP_NAME_PAST : OVERALL_PROP_NAME,
            },
          ],
          steps: behaviors?.map((_, i) => {
            return this._getMetricsForSegment(metrics, behaviors, i);
          }),
        },
      ];
    } else {
      // segments should only contain valid segment values. filter $overall
      const uniqueSegments = flattenSeriesToDepth(metrics, breakdownCount - 1);
      // TODO color
      return uniqueSegments.map(([path, leaf]) => ({
        prop: this.getSegmentLabelForPath(path),
        propName: this.getSegmentLabelForPath(path),
        props: path.map((seg) => ({propName: seg, prop: seg})), // TODO renames?
        steps: behaviors?.map((_, i) => this._getMetricsForSegment(leaf, behaviors, i)),
      }));
    }
  }

  private getCountType(): CountType {
    return getCountType(this.bookmark.sections.show);
  }

  private getMetricLabel(): string {
    const series = this.queryResults?.series;
    const funnelMetricLabel = Object.keys(series)[0];
    return funnelMetricLabel;
  }

  private shouldShowStatSig() {
    return (
      this.bookmark.sections.group.length > 0 &&
      !!this.bookmark.displayOptions.funnelStepsSelectedTableColumns?.[TableToggleableMetrics.StatSig]
    );
  }

  public getStepChartData(): StepChartData {
    const savedLegend: SavedLegend[] = this.bookmark?.legend?.filteredBreakdownSegments;
    const formattedDateRange = formatDateRange(
      {
        fromDate: this.queryResults.date_range?.from_date,
        toDate: this.queryResults.date_range?.to_date,
      },
      {isPartialDay: false, isMultiDay: true, includeYear: true},
    );
    const formattedTimeComparisonDateRange = formatDateRange(
      {
        fromDate: this.queryResults.time_comparison?.date_range?.from_date,
        toDate: this.queryResults.time_comparison?.date_range?.to_date,
      },
      {isPartialDay: false, isMultiDay: true, includeYear: true},
    );
    const comparisonDateRange = this.queryResults.time_comparison
      ? {
          from: getCalendarDate(this.queryResults.time_comparison?.date_range?.from_date),
          to: getCalendarDate(this.queryResults.time_comparison?.date_range?.to_date),
        }
      : null;
    const {funnelSegments, funnelCompareSegments, funnelLiftSegments} = this.getFunnelSegments();
    const funnelSegmentsForPopover = funnelSegments.flatMap((segment, segmentIdx) => {
      const comparisonSegment = funnelCompareSegments?.[segmentIdx];
      return comparisonSegment ? [comparisonSegment, segment] : [segment];
    });
    return {
      conversionSteps: this.getConversionSteps(),
      legendSegmentItems: this.getLegendSegmentItems(false),
      detailedLegendSegmentItems: this.getLegendSegmentItems(true),
      legendSegmentLabel: ``, // TODO
      savedLegend,
      mainDateRangeLabel: formattedDateRange,
      comparisonDateRangeLabel: this.queryResults.time_comparison && formattedTimeComparisonDateRange,
      comparisonDateRange,
      countType: this.getCountType(),
      funnelSegmentsForPopover,
      funnelSegments,
      funnelLiftSegments,
      funnelCompareSegments,
      funnelMetricLabel: this.getMetricLabel(),
    };
  }

  private getDefaultSortConfig(segmentationLevel: number): SortConfig {
    const DEFAULT_SORT_ATTRS: FlatSortConfig = {sortBy: SortBy.Value, sortOrder: SortOrder.Descending};
    const DEFAULT_SORT_CONFIG: SortConfig = {
      sortBy: SortBy.Column,
      colSortAttrs: Array(segmentationLevel).fill(DEFAULT_SORT_ATTRS),
    };
    return DEFAULT_SORT_CONFIG;
  }

  public getSorting(): SortConfig {
    const segmentationLevel = this.getBreakdownCount();
    const sortConfig = getSortConfig({
      bookmarkSortConfig: this.bookmark.sorting,
      liftComparison: this.bookmark.liftComparison,
      headerCount: segmentationLevel,
    })[ChartType.Bar];
    const result = sortConfig ?? this.getDefaultSortConfig(segmentationLevel);
    // special handling for funnel step chart. Step chart use same field for ChartType.Bar but it has ability to sort by individual valueField
    // while normal bar chart does not have this ability
    if (this.bookmark.displayOptions.chartType === ChartType.FunnelSteps) {
      result.valueField = this.bookmark.sorting?.[ChartType.Bar]?.valueField;
    }
    return result;
  }

  getSegmentLabelForPath(segments: string[]): string {
    return segments.join(` / `);
  }

  getLegendSegmentItems(detailedLabel: boolean): SegmentItem[] {
    // TODO re-use getSegmentLabelWithConversionRate? Or add lift comparison
    // first level is always tree
    const metrics = Object.values(this.queryResults.series)[0] as InsightsSeriesTree;
    const breakdownCount = this.getBreakdownCount();

    if (breakdownCount === 0) {
      const overall = Object.values(metrics[`overall_conv_ratio`]).sort();
      const conversionRate = overall[overall.length - 1][`all`];
      return [
        {
          id: OVERALL_PROP,
          label: `${OVERALL_PROP_NAME} • ${Math.round(conversionRate * 10000) / 100}%`,
        },
      ];
    } else {
      const uniqueSegments = flattenSeriesToDepth(metrics, breakdownCount - 1);
      let result = [];
      result = uniqueSegments
        .map(([path, leaf]) => {
          const key = this.getSegmentLabelForPath(path);
          const overall = Object.values(leaf[`overall_conv_ratio`]).sort();
          const overallConversion = overall[overall.length - 1][`all`];
          return {
            id: key,
            label: detailedLabel ? `${key} • ${Math.round(overallConversion * 10000) / 100}%` : key,
          };
        })
        .filter(({id}) => id !== OVERALL_PROP);
      return result;
    }
  }

  getSegmentColumnSpecs(bookmark: InsightsBookmarkParams): SegmentColumnSpec[] {
    let segmentColumns: SegmentColumnSpec[] = [];

    const behaviors = getFunnelsShowClauses(bookmark?.sections?.show || [])[0]?.behavior?.behaviors;
    behaviors.forEach((beh, idx) => {
      if (!!beh.behaviors && behaviors.length >= 2) {
        segmentColumns.push({
          title: `Step ` + (idx + 1) + ` Comparison`,
          segmentType: PropertyType.String,
        });
      }
    });
    const segments = getSelectedSegments(this.getBookmarkAsReport().sections.group.clauses);
    segmentColumns = segmentColumns.concat(
      segments.map((segment) => ({
        title: getSegmentDisplayName(segment),
        segmentType: getSegmentPropertyType(segment),
      })),
    );

    return segmentColumns;
  }

  getBookmarkAsReport() {
    // Utility to get the bookmark in report form (which has some serialization done on it)
    return Report.fromBookmark({params: this.bookmark, type: BookmarkType.Insights, name: ``, description: ``});
  }

  public getFunnelShowClause(): BehaviorShowClause {
    return getFunnelsShowClauses(this.bookmark?.sections?.show || [])[0];
  }

  public getDataTableInfo(params: {
    metricsResource: MetricsResource;
    onStatSigClick: StatSigClickHandler;
    stepChartData: StepChartData;
    tableWidth: number;
  }): {rows: FunnelTableRow[]; columnGroups: ColumnGroup[]; comparisonRows?: FunnelTableRow[]} {
    const {metricsResource, onStatSigClick, stepChartData, tableWidth} = params;
    // the "selected" color Map assigns "tbd" (an arbitrary invalid string) for all keys because the generated table row color is expected to be overridden by SegmentFilterController in <mp-segment-filter-data-table>
    // but getRows() is an older method (still used outside this widget) that expects a color string
    const selected = stepChartData.savedLegend?.length
      ? new Map<string, string>(stepChartData.savedLegend[0].selected.map((colorChoice) => [colorChoice.key, `tbd`]))
      : new Map();
    const {
      funnelSegments: segmentSteps,
      funnelCompareSegments: compareSegmentSteps,
      funnelLiftSegments: liftSegmentSteps,
    } = this.getFunnelSegments();
    const rows: FunnelTableRow[] = getRows({
      selected,
      controlGroup: (this.bookmark.displayOptions.statSigControl ?? []).map((segment) => segment.prop) ?? [],
      shouldShowStatSig: this.shouldShowStatSig(),
      segmentSteps,
      shouldFilterForSelected: false,
      liftComparison: this.bookmark.liftComparison,
      liftSegmentSteps,
      compareSegmentSteps,
    });

    const selectedColumns = this.bookmark.displayOptions.funnelStepsSelectedTableColumns || DEFAULT_STEPS_TABLE_COLUMNS;
    const segments = getSelectedSegments(this.getBookmarkAsReport().sections.group.clauses);
    const segmentSpec = this.getSegmentColumnSpecs(this.bookmark);
    const showClause = this.getFunnelShowClause();
    const behaviors = showClause?.behavior?.behaviors;
    const stepNames = behaviors.map((_, i) => this.getStepLabel(behaviors, i).label);
    const segmentTypes = getGroupByProperties(segments).map((prop) => prop && prop.propertyType);
    const columnGroups = getColumnGroups({
      liftType: this.bookmark.liftComparison?.type,
      metricsResource,
      onStatSigClick,
      segmentSpec,
      segmentTypes,
      selectedColumns,
      shouldShowStatSig: this.shouldShowStatSig(),
      showClause,
      showStepNamesInGroupHeaders: true,
      stepNames,
      tableWidth,
    });
    if (compareSegmentSteps && !liftSegmentSteps) {
      const comparisonRows = getRows({
        selected,
        controlGroup: null, // TODO event comparison stat sig?
        shouldShowStatSig: this.shouldShowStatSig(),
        segmentSteps: reconcileComparisonSegments({
          segments: segmentSteps,
          comparisonSegments: compareSegmentSteps,
          updatePropName: true,
        }),
        shouldFilterForSelected: false, // TODO download mode
      });
      return {rows, comparisonRows, columnGroups};
    }
    return {rows, columnGroups};
  }

  public getDataGroupId(): string {
    return this.bookmark.sections.globalDataGroupId;
  }

  public isEmptyReport(): boolean {
    // when swapping reports there is a brief period where the response is for the wrong chart
    // TOOD is there a better way to detect that or just not allow that?
    return !this.queryResults.series || Object.values(this.queryResults.series).length === 0;
  }

  public isEquivalent(bookmark: any, results: any): boolean {
    return isEqual(this.bookmark, bookmark) && isEqual(this.queryResults, results);
  }

  public getPropSumLabel(): Translatable {
    const prop = this._getPropSumProp();
    const resourceType = (
      prop.resourceType === InsightsResourceType.Formulas ? ResourceType.Formula : prop.resourceType
    ) as ResourceType;
    return ({t, print}) =>
      t(`chart-actions.tooltip.sum-of`, {
        property: print(propertiesUtils.renameProperty(this._getPropSumProp().name, resourceType, prop)),
      });
  }

  private getNumberOfEventComparisons(): number {
    const showClause = this.getFunnelShowClause();
    return getNumerOfEventComparisons(showClause);
  }

  private getBreakdownCount() {
    return this.bookmark.sections.group.length + this.getNumberOfEventComparisons();
  }

  private _getPropSumProp() {
    return getPropSumProp(this.bookmark.sections.show);
  }

  private _isPropSum() {
    return !!this._getPropSumProp();
  }

  private _getCountMetricName() {
    const valueKeyKey = this.getFunnelShowClause()?.measurement?.math;

    // conversion rate special cased since we already have separate columns for it
    if (valueKeyKey in MATH_TO_FUNNEL_STEPS_METRIC && this.getCountType() !== CountType.ConversionRate) {
      return MATH_TO_FUNNEL_STEPS_METRIC[valueKeyKey];
    } else if (this._isPropSum()) {
      return `property_sum`;
    } else {
      return `count`;
    }
  }
  private _getMetric(segment, queryLabel, metricName) {
    return segment[metricName]?.[queryLabel]?.[`all`];
  }
}
